import React, {useEffect, useMemo, useState} from "react";
import {
    download,
    generateCsv,
    mkConfig,
} from "export-to-csv";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
      CircularProgress,
    FormControlLabel,
    Stack,
} from "@mui/material";
import * as XLSX from "xlsx";
import type {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";
import {ValVersOreDTO} from "./types";
import {useGetValVersOreDTO} from "./Services";

interface ValVersOreExpProps {
    pagination: MRT_PaginationState;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    sorting: MRT_SortingState;
    columnVisibility: Record<string, boolean>;
    columns: MRT_ColumnDef<ValVersOreDTO>[];
    onClose: () => void;
}

type ExportRow = Record<
    string,
    string | number | boolean
>;

const EXPORT_PAGINATION: MRT_PaginationState = {
    pageIndex: 0,
    pageSize: 100000,
};

const ValVersOreExp: React.FC<ValVersOreExpProps> = ({
                                                         columnFilters,
                                                         globalFilter,
                                                         sorting,
                                                         columnVisibility,
                                                         columns,
                                                         onClose,
                                                     }) => {
    /*
     * enabled = false:
     * la query non parte all'apertura del componente.
     *
     * Verrà eseguita manualmente tramite refetch().
     */
    const {
        refetch,
        isError,
    } = useGetValVersOreDTO(
        EXPORT_PAGINATION,
        columnFilters,
        globalFilter,
        sorting,
        false
    );

    const fieldDefinitions = useMemo(
        () =>
            columns
                .filter((column) => {
                    if (!column.accessorKey) {
                        return false;
                    }

                    const accessorKey = String(column.accessorKey);

                    return columnVisibility[accessorKey] !== false;
                })
                .map((column) => ({
                    key: column.accessorKey as keyof ValVersOreDTO,
                    label:
                        typeof column.header === "string"
                            ? column.header
                            : String(column.accessorKey),
                })),
        [columns, columnVisibility]
    );

    const [selectedFields, setSelectedFields] = useState<
        (keyof ValVersOreDTO)[]
    >([]);

    /*
     * Alla prima apertura seleziona tutte le colonne visibili.
     *
     * Se fieldDefinitions cambia, mantiene solo le colonne
     * precedentemente selezionate che sono ancora disponibili.
     */
    useEffect(() => {
        if (fieldDefinitions.length === 0) {
            return;
        }

        setSelectedFields((previousFields) => {
            const availableFields = fieldDefinitions.map(
                (field) => field.key
            );

            if (previousFields.length === 0) {
                return availableFields;
            }

            return previousFields.filter((field) =>
                availableFields.includes(field)
            );
        });
    }, [fieldDefinitions]);

    const csvConfig = useMemo(
        () =>
            mkConfig({
                filename: "ValVersOre",
                fieldSeparator: ";",
                decimalSeparator: ",",
                useKeysAsHeaders: true,
                showColumnHeaders: true,
            }),
        []
    );



    type ExportType = "csv" | "excel" | null;
    const [exporting, setExporting] = useState<ExportType>(null);
    const isExporting = exporting !== null;


    const exportDisabled =
        isExporting ||
        fieldDefinitions.length === 0 ||
        selectedFields.length === 0;





    const handleFieldToggle = (
        field: keyof ValVersOreDTO
    ) => {
        setSelectedFields((previousFields) =>
            previousFields.includes(field)
                ? previousFields.filter(
                    (currentField) => currentField !== field
                )
                : [...previousFields, field]
        );
    };

    const formatDateIT = (value: unknown): string => {
        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return "";
        }

        const date = new Date(String(value));

        if (Number.isNaN(date.getTime())) {
            return String(value);
        }

        return date.toLocaleDateString("it-IT");
    };

    const formatExportValue = (
        key: keyof ValVersOreDTO,
        value: unknown
    ): string | number | boolean => {
        if (key === "datareg" || key === "datains") {
            return formatDateIT(value);
        }

        if (value === null || value === undefined) {
            return "";
        }

        if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
        ) {
            return value;
        }

        return String(value);
    };

    const generateExportData = (
        results: ValVersOreDTO[]
    ): ExportRow[] => {
        return results.map((item) => {
            const rowData: ExportRow = {};

            fieldDefinitions.forEach(({key, label}) => {
                if (!selectedFields.includes(key)) {
                    return;
                }

                rowData[label] = formatExportValue(
                    key,
                    item[key]
                );
            });

            return rowData;
        });
    };

    const getExportResults = async (): Promise<
        ValVersOreDTO[]
    > => {
        const response = await refetch();

        if (response.isError) {
            throw response.error;
        }

        return response.data?.results ?? [];
    };

   const handleExportExcel = async () => {
  if (exportDisabled) {
    return;
  }

  setExporting("excel");

  try {
    const response = await refetch();

    if (response.isError) {
      throw response.error;
    }

    const results = response.data?.results ?? [];

    if (results.length === 0) {
      return;
    }

    const excelData = generateExportData(results);
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "ValVersOre"
    );

    XLSX.writeFile(workbook, "ValVersOre.xlsx");
  } catch (error) {
    console.error(
      "Errore durante l'esportazione Excel:",
      error
    );
  } finally {
    setExporting(null);
  }
};

const handleExportCSV = async () => {
  if (exportDisabled) {
    return;
  }

  setExporting("csv");

  try {
    const response = await refetch();

    if (response.isError) {
      throw response.error;
    }

    const results = response.data?.results ?? [];

    if (results.length === 0) {
      return;
    }

    const selectedData = generateExportData(results);
    const csv = generateCsv(csvConfig)(selectedData);

    download(csvConfig)(csv);
  } catch (error) {
    console.error(
      "Errore durante l'esportazione CSV:",
      error
    );
  } finally {
    setExporting(null);
  }
};

    return (

<Dialog
  onClose={isExporting ? undefined : onClose}
  open
  fullWidth
  maxWidth="md"
>
  <Box sx={{ position: "relative" }}>
    {isExporting && (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(1px)",
        }}
      >
        <CircularProgress />
      </Box>
    )}

    <DialogContent>
      <h4>Seleziona campi da esportare:</h4>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
            lg: "repeat(8, 1fr)",
          },
          gap: 1,
        }}
      >
        {fieldDefinitions.map(({ key, label }) => (
          <FormControlLabel
            key={String(key)}
            control={
              <Checkbox
                checked={selectedFields.includes(key)}
                onChange={() => handleFieldToggle(key)}
              />
            }
            label={label}
          />
        ))}
      </Box>
    </DialogContent>

    <DialogContent>
      <Stack
        direction={{
          xs: "column",
          md: "row",
        }}
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Button
          fullWidth
          color="primary"
          variant="outlined"
          onClick={() =>
            setSelectedFields(
              fieldDefinitions.map((field) => field.key)
            )
          }
        >
          SELEZIONA TUTTI
        </Button>

        <Button
          fullWidth
          color="primary"
          variant="outlined"
          onClick={() => setSelectedFields([])}
        >
          DESELEZ. TUTTI
        </Button>

        <Button
          fullWidth
          color="primary"
          variant="contained"
          onClick={handleExportCSV}
          disabled={
            fieldDefinitions.length === 0 ||
            selectedFields.length === 0
          }
        >
          EXPORT TO CSV
        </Button>

        <Button
          fullWidth
          color="primary"
          variant="contained"
          onClick={handleExportExcel}
          disabled={
            fieldDefinitions.length === 0 ||
            selectedFields.length === 0
          }
        >
          EXPORT TO EXCEL
        </Button>

        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          color="error"
        >
          CHIUDI
        </Button>
      </Stack>
    </DialogContent>
  </Box>
</Dialog>

    );
};

export default ValVersOreExp;

